import MainLayout from 'layouts/MainLayout';
import { Navigate, RouteObject, createBrowserRouter } from 'react-router-dom';
import { DATA_ACCESS_PATHS } from 'shiplab-components/data-access/paths';
import DataAccessApiTokensPage from 'shiplab-components/data-access/DataAccessApiTokensPage';
import DataAccessDataLakePage from 'shiplab-components/data-access/DataAccessDataLakePage';
import DataAccessMcpServerPage from 'shiplab-components/data-access/DataAccessMcpServerPage';
import Home from 'pages/dashboard/ecommerce';
import MainLayoutProvider from 'providers/MainLayoutProvider';
import Default from 'pages/pages/landing/Default';
import Error404 from 'pages/error/Error404';
import Error403 from 'pages/error/Error403';
import Error500 from 'pages/error/Error500';
import ECharts from 'pages/modules/charts/ECharts';
import App from 'App';
import CardSignIn from 'pages/pages/authentication/card/SignIn';
import CardSignUp from 'pages/pages/authentication/card/SignUp';
import CardForgotPassword from 'pages/pages/authentication/card/ForgotPassword';
import CardSignOut from 'pages/pages/authentication/card/SignOut';
import CardResetPassword from 'pages/pages/authentication/card/ResetPassword';
import CardTwoFA from 'pages/pages/authentication/card/TwoFA';
import CardLockScreen from 'pages/pages/authentication/card/LockScreen';
import SplitSignIn from 'pages/pages/authentication/split/SignIn';
import SplitSignUp from 'pages/pages/authentication/split/SignUp';
import SplitSignOut from 'pages/pages/authentication/split/SignOut';
import SplitForgotPassword from 'pages/pages/authentication/split/ForgotPassword';
import SplitResetPassword from 'pages/pages/authentication/split/ResetPassword';
import SplitLockScreen from 'pages/pages/authentication/split/LockScreen';
import SplitTwoFA from 'pages/pages/authentication/split/TwoFA';
import { Suspense, lazy } from 'react';
import Alternate from 'pages/pages/landing/Alternate';
import Connection from 'shiplab-components/Connection';
import AuthRoute from 'shiplab-components/AuthRoute';
import PrivateRoute from 'shiplab-components/PrivateRoute';
import ConnectorGroupManagementPage from 'shiplab-components/ConnectorGroupManagementPage';
import ConnectorGroupConnectorManagementPage from 'shiplab-components/ConnectorManagementPage';
import ConnectorManagementCustomerPage from 'shiplab-components/ConnectorManagementCustomerPage';
import InvoicesManagementPage from 'shiplab-components/Invoices';
// import EfficacyReportPage from 'shiplab-components/EfficacyReportPage'
// import ConnectorReport from 'shiplab-components/ConnectorReport';
import Profile from 'shiplab-components/Profile';
import PlansBillingPage from 'shiplab-components/PlansBillingPage';
import PlansBillingCompletePage from 'shiplab-components/PlansBillingCompletePage';
import ServiceAgreementPage from 'shiplab-components/ServiceAgreementPage';
import { routes as sitemapRoutes } from 'sitemap';

const FontAwesomeExample = lazy(
  () => import('pages/modules/components/FontAwesomeExample')
);

const FeatherIconsExample = lazy(
  () => import('pages/modules/components/FeatherIconsExample')
);

const UniconsExample = lazy(
  () => import('pages/modules/components/UniconsExample')
);

const DataAccessRestApiPage = lazy(
  () => import('shiplab-components/data-access/DataAccessRestApiPage'),
);


const routes: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        path: '/',
        element: (
          <PrivateRoute>
            <MainLayoutProvider>
              <MainLayout routes={sitemapRoutes} />
            </MainLayoutProvider>
          </PrivateRoute>
        ),
        children: [
          {
            index: true,
            element: <Home />
          },
          // {
          //   path: 'efficacy-Report',
          //   element: <EfficacyReportPage />
          // },
          // {
          //   path: 'connector-report',
          //   element: <ConnectorReport />
          // },
          {
            path: 'Data-Access',
            element: <Connection />,
            children: [
              {
                index: true,
                element: <Navigate to={DATA_ACCESS_PATHS.apiTokens} replace />,
              },
              {
                path: 'api-tokens',
                element: <DataAccessApiTokensPage />,
              },
              {
                path: 'data-lake',
                element: <DataAccessDataLakePage />,
              },
              {
                path: 'rest-api',
                element: (
                  <Suspense fallback={null}>
                    <DataAccessRestApiPage />
                  </Suspense>
                ),
              },
              {
                path: 'mcp-server',
                element: <DataAccessMcpServerPage />,
              },
            ],
          },

          {
            path: 'group-management',
            element: <ConnectorGroupManagementPage />
          },
          {
            path: 'group-connectors',
            element: <ConnectorGroupConnectorManagementPage />
          },
          {
            path: 'connectorGroup-connector-management',
            element: <ConnectorGroupConnectorManagementPage />
          },
          {
            path: 'connector-management',
            element: <ConnectorManagementCustomerPage />
          },
          {
            path: 'explore',
            element: <InvoicesManagementPage />
          },
          {
            path: 'profile',
            element: <Profile />
          },
          {
            path: 'pages/plans-billing',
            element: <PlansBillingPage />
          },
          {
            path: 'pages/plans-billing/complete',
            element: <PlansBillingCompletePage />
          },
          {
            path: 'charts',
            children: [
              {
                path: 'e-charts',
                element: <ECharts />
              }
            ]
          },
        ]
      },
      {
        path: 'pages/landing',
        children: [
          {
            path: 'default',
            element: <Default />
          },
          {
            path: 'alternate',
            element: <Alternate />
          }
        ]
      },
      {
        path: '/pages/errors/',
        children: [
          {
            path: '404',
            element: <Error404 />
          },
          {
            path: '403',
            element: <Error403 />
          },
          {
            path: '500',
            element: <Error500 />
          }
        ]
      },
      {
        path: '/pages/authentication/simple/',
        children: [
          {
            path: 'sign-in',
            element: (
              <Navigate to="/pages/authentication/card/sign-in" replace />
            )
          },
          {
            path: 'sign-up',
            element: (
              <Navigate to="/pages/authentication/card/sign-up" replace />
            )
          },
          {
            path: 'sign-out',
            element: (
              <Navigate to="/pages/authentication/card/sign-out" replace />
            )
          },
          {
            path: 'forgot-password',
            element: (
              <Navigate
                to="/pages/authentication/card/forgot-password"
                replace
              />
            )
          },
          {
            path: 'reset-password',
            element: (
              <Navigate
                to="/pages/authentication/card/reset-password"
                replace
              />
            )
          },
          {
            path: 'lock-screen',
            element: (
              <Navigate to="/pages/authentication/card/lock-screen" replace />
            )
          },
          {
            path: '2FA',
            element: <Navigate to="/pages/authentication/card/2FA" replace />
          }
        ]
      },
      {
        path: '/pages/authentication/split/',
        element: <AuthRoute/>,
        children: [
          {
            path: 'sign-in',
            element: <SplitSignIn />
          },
          {
            path: 'sign-up',
            element: <SplitSignUp />
          },
          {
            path: 'sign-out',
            element: <SplitSignOut />
          },
          {
            path: 'forgot-password',
            element: <SplitForgotPassword />
          },
          {
            path: 'reset-password',
            element: <SplitResetPassword />
          },
          {
            path: 'lock-screen',
            element: <SplitLockScreen />
          },
          {
            path: '2FA',
            element: <SplitTwoFA />
          }
        ]
      },
      {
        path: '/pages/authentication/card/',
        element: <AuthRoute/>,
        children: [
          {
            path: 'sign-in',
            element: <CardSignIn />
          },
          {
            path: 'sign-up',
            element: <CardSignUp />
          },
          {
            path: 'sign-out',
            element: <CardSignOut />
          },
          {
            path: 'forgot-password',
            element: <CardForgotPassword />
          },
          {
            path: 'reset-password',
            element: <CardResetPassword />
          },
          {
            path: 'lock-screen',
            element: <CardLockScreen />
          },
          {
            path: '2FA',
            element: <CardTwoFA />
          }
        ]
      },
      {
        path: 'legal/service-agreement',
        element: <ServiceAgreementPage />
      },
      {
        path: '*',
        element: <Error404 />
      }
    ]
  }
];

export let router = createBrowserRouter(routes);

export default routes;
